import type { ApiClient } from './index'
import type { SlackIdentity } from './slack'

export interface TeamSlackChannel {
  channelId: string
  channelName?: string
  role?: 'primary' | 'secondary'
}

export type TeamMemberRole = 'lead' | 'member' | 'reviewer' | 'advisor'

export interface TeamMember {
  userId: string
  email: string
  name?: string
  role: TeamMemberRole
  joinedAt: string
}

/** Provenance for a team document that was created from the sub-team catalog. */
export interface PrefillSource {
  subTeamKey: string
  confidence: string
  generatedAt: string
  version: string
}

export interface BackendTeam {
  teamId: string
  name: string
  workspaceKind?: 'team' | 'org_workspace'
  orgSlug?: string
  description?: string
  avatar?: string
  slackChannels: TeamSlackChannel[]
  githubRepos: string[]
  grafanaSubteam?: string
  clickupSpaceId?: string
  clickupSpaceName?: string
  clickupFolderId?: string
  clickupFolderName?: string
  members: TeamMember[]
  enabled: boolean
  createdAt: string
  updatedAt: string
  archivedAt?: string
  prefillSource?: PrefillSource
}

export interface CreateTeamInput {
  /**
   * Required catalog key. The backend derives the squad name as
   * "<parentTeam> <subTeam>" from the catalog; hand-typed names are not
   * supported through this endpoint anymore.
   */
  subTeamKey: string
  description?: string
  avatar?: string
  slackChannels?: TeamSlackChannel[]
  githubRepos?: string[]
  enabled?: boolean
  /** @deprecated Ignored by the backend — name is derived from the catalog. */
  name?: string
}

/**
 * One row in the bundled sub-team catalog. Mirrors the BE
 * `SubteamCatalogRow` shape but is colocated with the api client so the FE
 * doesn't need to depend on backend types.
 */
export interface SubteamCatalogRow {
  subTeamKey: string
  parentTeam: string
  subTeam: string
  displayName: string
  confidence: string
  grafanaSubteam: string
  githubRepos: string[]
  slackChannel: { channelId: string; channelName: string } | null
  clickupFolder: {
    id: string
    name: string
    spaceId: string
    spaceName: string
  } | null
}

export interface SubteamCatalogResponse {
  generatedAt: string
  version: string
  rowCount: number
  rows: SubteamCatalogRow[]
}

/**
 * Picker-friendly preview projection of a catalog row. The picker UI uses
 * this for chip rendering ("3 repos · #ai-agent-studio · ClickUp folder").
 */
export interface SubteamPreview {
  subTeamKey: string
  displayName: string
  parentTeam: string
  subTeam: string
  confidence: string
  repoCount: number
  repos: string[]
  slackChannelName: string | null
  slackChannelId: string | null
  clickupFolderName: string | null
  grafanaSubteam: string
}

export interface PatchTeamInput {
  name?: string
  description?: string
  avatar?: string
  slackChannels?: TeamSlackChannel[]
  githubRepos?: string[]
  grafanaSubteam?: string
  clickupSpaceId?: string
  clickupSpaceName?: string
  clickupFolderId?: string
  clickupFolderName?: string
  enabled?: boolean
}

export type WorkloadStatus = 'healthy' | 'warning' | 'critical'

export interface ServiceHealth {
  container: string
  rpm: number
  errorRate: number
  p95Ms: number
  status: WorkloadStatus
}

export interface WorkerHealth {
  app: string
  container: string
  messageRate: number
  errorRate: number
  podCount: number
  status: WorkloadStatus
}

export interface FrontendApp {
  repo: string
  appName: string
  appPath: string
  githubUrl: string
}

export interface WorkloadsResponse {
  grafanaSubteam: string | null
  services: ServiceHealth[]
  workers: WorkerHealth[]
  frontendApps: FrontendApp[]
}

export interface TeamAgent {
  slug: string
  name: string
  description: string
  model: string
  category: string
  role: string
  color: string
  capabilities: string[]
  skills: string[]
}

export interface AddMemberInput {
  userId: string
  email: string
  name?: string
  role: TeamMemberRole
}

export interface PatchMemberInput {
  role?: TeamMemberRole
  name?: string
}

export interface PullSummary {
  teamId: string
  repo: string
  prsFetched: number
  issuesFetched: number
  signalsIngested: number
  errors: string[]
}

export interface DiscoverTeamsInput {
  githubOrg?: string
  slackToken?: string
  slackWorkspaceId?: string
  prefixDepth?: number
  /** Use Grafana-first discovery (queries http_requests_total by sub_team label). */
  grafana?: boolean
  /** Grafana team label to query, e.g. "revex". Required when grafana=true. */
  grafanaTeam?: string
}

export interface DiscoveredTeam {
  suggestedName: string
  slackChannels: { channelId: string; channelName: string }[]
  githubRepos: string[]
  /** Canonical Grafana sub_team slug (only present in Grafana-first results). */
  subTeam?: string
}

export interface SeedTeamInput {
  name: string
  slackChannels?: { channelId: string; channelName: string }[]
  githubRepos?: string[]
}

export interface SeedTeamsResult {
  seeded: BackendTeam[]
  errors: string[]
}

type Headers = Record<string, string>

function identityHeaders(id: SlackIdentity): Headers {
  return { 'x-user-id': id.userId, 'x-location-id': id.locationId }
}

async function request<T>(
  client: ApiClient,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  id: SlackIdentity,
  body?: unknown
): Promise<T> {
  const res = await client.fetchWithAuth(path, {
    method,
    body: body !== undefined ? JSON.stringify(body) : undefined
  }, identityHeaders(id))
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`${method} ${path} failed: ${res.status} ${text}`)
  }
  return res.json() as Promise<T>
}

export class TeamsApi {
  constructor(private readonly client: ApiClient) {}

  list(id: SlackIdentity, enabledOnly = false, includeArchived = false): Promise<BackendTeam[]> {
    const params = new URLSearchParams()
    if (enabledOnly) params.set('enabledOnly', 'true')
    if (includeArchived) params.set('includeArchived', 'true')
    const qs = params.toString()
    return request<BackendTeam[]>(this.client, 'GET', `/teams${qs ? `?${qs}` : ''}`, id)
  }

  get(id: SlackIdentity, teamId: string): Promise<BackendTeam> {
    return request<BackendTeam>(this.client, 'GET', `/teams/${encodeURIComponent(teamId)}`, id)
  }

  create(id: SlackIdentity, input: CreateTeamInput): Promise<BackendTeam> {
    return request<BackendTeam>(this.client, 'POST', '/teams', id, input)
  }

  patch(id: SlackIdentity, teamId: string, patch: PatchTeamInput): Promise<BackendTeam> {
    return request<BackendTeam>(
      this.client,
      'PATCH',
      `/teams/${encodeURIComponent(teamId)}`,
      id,
      patch
    )
  }

  delete(id: SlackIdentity, teamId: string): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(
      this.client,
      'DELETE',
      `/teams/${encodeURIComponent(teamId)}`,
      id
    )
  }

  pullGithub(
    id: SlackIdentity,
    teamId?: string
  ): Promise<{ ok: boolean; summaries: PullSummary[] }> {
    return request<{ ok: boolean; summaries: PullSummary[] }>(
      this.client,
      'POST',
      '/ingest/github/pull',
      id,
      teamId ? { teamId } : {}
    )
  }

  listMembers(id: SlackIdentity, teamId: string): Promise<TeamMember[]> {
    return request<TeamMember[]>(
      this.client,
      'GET',
      `/teams/${encodeURIComponent(teamId)}/members`,
      id
    )
  }

  addMember(id: SlackIdentity, teamId: string, input: AddMemberInput): Promise<BackendTeam> {
    return request<BackendTeam>(
      this.client,
      'POST',
      `/teams/${encodeURIComponent(teamId)}/members`,
      id,
      input
    )
  }

  patchMember(
    id: SlackIdentity,
    teamId: string,
    userId: string,
    patch: PatchMemberInput
  ): Promise<BackendTeam> {
    return request<BackendTeam>(
      this.client,
      'PATCH',
      `/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
      id,
      patch
    )
  }

  removeMember(id: SlackIdentity, teamId: string, userId: string): Promise<BackendTeam> {
    return request<BackendTeam>(
      this.client,
      'DELETE',
      `/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
      id
    )
  }

  listAgentsCatalog(id: SlackIdentity): Promise<TeamAgent[]> {
    return request<TeamAgent[]>(this.client, 'GET', '/teams/agents-catalog', id)
  }

  listTeamAgents(id: SlackIdentity, teamId: string): Promise<TeamAgent[]> {
    return request<TeamAgent[]>(
      this.client,
      'GET',
      `/teams/${encodeURIComponent(teamId)}/agents`,
      id,
    )
  }

  updateTeamAgents(id: SlackIdentity, teamId: string, agentSlugs: string[]): Promise<BackendTeam> {
    return request<BackendTeam>(
      this.client,
      'PUT',
      `/teams/${encodeURIComponent(teamId)}/agents`,
      id,
      { agentSlugs },
    )
  }

  discover(id: SlackIdentity, input: DiscoverTeamsInput): Promise<DiscoveredTeam[]> {
    return request<DiscoveredTeam[]>(this.client, 'POST', '/teams/discover', id, input)
  }

  seed(id: SlackIdentity, teams: SeedTeamInput[]): Promise<SeedTeamsResult> {
    return request<SeedTeamsResult>(this.client, 'POST', '/teams/seed', id, { teams })
  }

  workloads(id: SlackIdentity, teamId: string): Promise<WorkloadsResponse> {
    return request<WorkloadsResponse>(
      this.client,
      'GET',
      `/teams/${encodeURIComponent(teamId)}/workloads`,
      id
    )
  }

  getSubteamCatalog(id: SlackIdentity): Promise<SubteamCatalogResponse> {
    return request<SubteamCatalogResponse>(this.client, 'GET', '/teams/subteam-catalog', id)
  }
}

/** Project a catalog row into the picker-friendly preview shape. */
export function toSubteamPreview(row: SubteamCatalogRow): SubteamPreview {
  return {
    subTeamKey: row.subTeamKey,
    displayName: row.displayName,
    parentTeam: row.parentTeam,
    subTeam: row.subTeam,
    confidence: row.confidence,
    repoCount: row.githubRepos.length,
    repos: row.githubRepos,
    slackChannelName: row.slackChannel?.channelName ?? null,
    slackChannelId: row.slackChannel?.channelId ?? null,
    clickupFolderName: row.clickupFolder?.name ?? null,
    grafanaSubteam: row.grafanaSubteam,
  }
}

export function createTeamsApi(client: ApiClient): TeamsApi {
  return new TeamsApi(client)
}
